import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useUIStore } from '../stores';
import { API_URL, formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Plus, Building2, Trash2, ArrowRightLeft, Loader2,
  Eye, EyeOff, AlertTriangle, CheckCircle2,
  Paperclip, X, Repeat, History, PlayCircle, StopCircle, Send, BellRing, Settings2
} from 'lucide-react';

const SOURCES = [
  { value: 'website', label: 'Site web' },
  { value: 'referral', label: 'Recommandation' },
  { value: 'social', label: 'Réseaux sociaux' },
  { value: 'event', label: 'Salon / Événement' },
  { value: 'cold_call', label: 'Démarchage' },
  { value: 'other', label: 'Autre' },
];

// --- COMPOSANT CARTE PROSPECT ---
const LeadCard = ({ lead, index, onEdit, onDelete, onManualRelance, onDismissAlert, manualFollowupDelayDays, isSelected, onToggleSelect }) => {
  const leadId = lead.id || lead._id;

  const getFollowupStatus = () => {
    if (lead.auto_followup_active) return { type: 'auto', color: 'border-l-blue-400' };
    if (lead.status === 'won' || lead.status === 'lost') return { type: 'ok', color: 'border-l-transparent' };

    const lastContactDate = lead.last_followup_date ? new Date(lead.last_followup_date) : new Date(lead.created_at || Date.now());
    const today = new Date();
    const diffTime = Math.abs(today - lastContactDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= manualFollowupDelayDays) {
      return { type: 'alert', color: 'border-l-red-500', days: diffDays };
    }
    return { type: 'ok', color: 'border-l-emerald-500' };
  };

  const status = getFollowupStatus();

  return (
    <Draggable draggableId={leadId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`mb-3 transition-transform ${snapshot.isDragging ? 'z-50' : ''}`}
          style={{ ...provided.draggableProps.style }}
        >
          <Card className={`relative cursor-grab active:cursor-grabbing select-none group hover:shadow-md border-l-4 ${status.color} ${snapshot.isDragging ? 'shadow-xl ring-2 ring-primary/30' : ''} ${isSelected ? 'bg-primary/5 border-r-primary border-t-primary border-b-primary ring-1 ring-primary/50' : ''}`}>

            {/* LA CLOCHE CLIQUABLE */}
            {status.type === 'alert' && !isSelected && (
              <div
                className="absolute -top-2 -right-2 bg-red-100 dark:bg-red-900/30 rounded-full p-1 shadow-sm animate-pulse border border-red-200 cursor-pointer hover:bg-red-200 hover:scale-110 transition-all z-10"
                title={`Aucun contact depuis ${status.days} jours. Cliquez pour marquer comme traité.`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onDismissAlert(leadId);
                }}
              >
                <BellRing className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
            )}

            <CardContent className="p-3">
              <div className="flex items-start gap-2">

                {/* CHECKBOX DE SÉLECTION MULTIPLE */}
                <div
                  className="mt-0.5 cursor-pointer z-10"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onToggleSelect(leadId); }}
                >
                  <Checkbox checked={isSelected} className={isSelected ? "" : "opacity-0 group-hover:opacity-100 transition-opacity"} />
                </div>

                <div className="flex-1 min-w-0" onClick={() => onEdit(lead)}>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate pr-2">{lead.name}</p>
                    {status.type === 'auto' && <PlayCircle className="w-3 h-3 text-blue-500 shrink-0" title="Relance automatique active" />}
                  </div>

                  {lead.company && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Building2 className="w-3 h-3" /> {lead.company}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-1 mt-2">
                    {lead.estimated_value > 0 && (
                      <span className="text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                        {formatCurrency(lead.estimated_value)}
                      </span>
                    )}
                    {lead.source && (
                      <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground h-5">
                        {lead.source}
                      </Badge>
                    )}
                  </div>

                  {(lead.followup_count > 0 || lead.last_followup_date) && (
                    <div className="mt-3 pt-2 border-t border-dashed flex items-center justify-between text-[10px] text-muted-foreground">
                      <div className="flex items-center gap-2">
                        {lead.followup_count > 0 && (
                          <span className="flex items-center" title="Nb Relances"><Repeat className="w-3 h-3 mr-1" /> {lead.followup_count}</span>
                        )}
                        {lead.last_followup_date && (
                          <span className="flex items-center" title="Dernière relance"><History className="w-3 h-3 mr-1" /> {new Date(lead.last_followup_date).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })}</span>
                        )}
                      </div>
                      {status.type === 'alert' && (
                        <span className="text-red-500 font-medium">Action requise</span>
                      )}
                    </div>
                  )}
                </div>

                {/* BOUTONS D'ACTION AU SURVOL */}
                {!isSelected && (
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost" size="icon"
                      className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-50"
                      title="Marquer comme contacté aujourd'hui"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onDismissAlert(leadId); }}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className={`h-6 w-6 ${!lead.email ? 'text-gray-300 cursor-not-allowed' : 'text-blue-400 hover:text-blue-600 hover:bg-blue-50'}`}
                      title={lead.email ? "Envoyer un email de relance" : "Pas d'email"}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (lead.email) onManualRelance(leadId);
                      }}
                    >
                      <Send className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-red-50"
                      title="Supprimer"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onDelete(leadId); }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Draggable>
  );
};

// --- COMPOSANT COLONNE KANBAN ---
const KanbanColumn = ({ column, leads, onEdit, onDelete, onManualRelance, onDismissAlert, isOrphan, manualFollowupDelayDays, selectedLeads, onToggleSelect }) => {
  const total = leads.reduce((s, lead) => s + (lead.estimated_value || 0), 0);

  return (
    <div className="flex flex-col h-full min-w-[300px] max-w-[320px] bg-slate-50/50 dark:bg-slate-900/30 border rounded-xl overflow-hidden" data-testid={`kanban-column-${column.id}`}>
      <div className="p-3 bg-white dark:bg-slate-900 border-b shrink-0">
        <div className={`flex items-center gap-2 ${isOrphan ? 'text-red-600' : ''}`}>
          {isOrphan ? (
            <AlertTriangle className="w-4 h-4" />
          ) : (
            <div className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ backgroundColor: column.color }} />
          )}
          <h3 className="text-sm font-medium truncate" title={column.name}>{column.name}</h3>
          <Badge variant={isOrphan ? "destructive" : "secondary"} className="text-xs ml-auto">
            {leads.length}
          </Badge>
        </div>
        {total > 0 && (
          <p className="text-xs text-muted-foreground mt-1">{formatCurrency(total)}</p>
        )}
      </div>

      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`
                flex-1 overflow-y-auto min-h-0 p-3 transition-colors duration-150
                [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-200 hover:[&::-webkit-scrollbar-thumb]:bg-slate-300 dark:[&::-webkit-scrollbar-thumb]:bg-slate-700
                ${snapshot.isDraggingOver ? 'bg-primary/5 shadow-inner' : ''}
                ${isOrphan ? 'bg-red-50/20' : ''}
            `}
          >
            {leads.map((lead, index) => (
              <LeadCard
                key={lead.id || lead._id}
                lead={lead}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
                onManualRelance={onManualRelance}
                onDismissAlert={onDismissAlert}
                manualFollowupDelayDays={manualFollowupDelayDays}
                isSelected={selectedLeads.includes(lead.id || lead._id)}
                onToggleSelect={onToggleSelect}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// --- DIALOGUE LEAD ---
const LeadFormDialog = ({ lead, open, onOpenChange, onSave, columns }) => {
  const defaultStatus = columns && columns.length > 0 ? columns[0].id : '';

  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', source: 'none',
    estimated_value: '', notes: '', status: '',
    auto_followup_active: false, followup_frequency_days: 7, max_followups: 3
  });

  useEffect(() => {
    if (open) {
      if (lead) {
        setForm({
          ...lead,
          source: lead.source || 'none',
          estimated_value: lead.estimated_value?.toString() || '',
          auto_followup_active: lead.auto_followup_active || false,
          followup_frequency_days: lead.followup_frequency_days || 7,
          max_followups: lead.max_followups || 3,
          status: lead.status || defaultStatus,
          notes: ''
        });
      } else {
        setForm({
          name: '', company: '', email: '', phone: '', source: 'none',
          estimated_value: '', notes: '', status: defaultStatus,
          auto_followup_active: false, followup_frequency_days: 7, max_followups: 3
        });
      }
    }
  }, [lead, open, columns, defaultStatus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: form.name,
      company: form.company || null,
      email: form.email || null,
      phone: form.phone || null,
      source: form.source === 'none' ? null : form.source,
      estimated_value: parseFloat(form.estimated_value) || 0,
      status: form.status,
      auto_followup_active: form.auto_followup_active,
      followup_frequency_days: parseInt(form.followup_frequency_days) || 7,
      max_followups: parseInt(form.max_followups) || 3,
      notes: form.notes && form.notes.trim() !== '' ? form.notes : null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{lead ? 'Modifier le lead' : 'Nouveau lead'}</DialogTitle>
          <DialogDescription>Informations du prospect</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Nom / Contact *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Entreprise</Label><Input value={form.company || ''} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Téléphone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Source</Label>
              <Select value={form.source || 'none'} onValueChange={v => setForm({ ...form, source: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non définie</SelectItem>
                  {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Valeur estimée (€)</Label><Input type="number" step="0.01" value={form.estimated_value} onChange={e => setForm({ ...form, estimated_value: e.target.value })} /></div>
          </div>
          <div className="space-y-2">
            <Label>Étape du pipeline</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {columns && columns.map(col => (
                  <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                ))}
                {!columns.find(c => c.id === form.status) && form.status && (
                  <SelectItem value={form.status} disabled>⚠️ Actuel: {form.status}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-4 border rounded-lg p-4 bg-muted/20">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5"><Label className="text-base">Relance Automatique</Label></div>
              <Switch checked={form.auto_followup_active} onCheckedChange={(c) => setForm({ ...form, auto_followup_active: c })} />
            </div>
            {form.auto_followup_active && (
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label>Fréquence (jours)</Label>
                  <Input type="number" min="1" value={form.followup_frequency_days} onChange={(e) => setForm({ ...form, followup_frequency_days: e.target.value })} />
                </div>
                <div className="flex-1">
                  <Label>Max Relances</Label>
                  <Input type="number" min="1" max="10" value={form.max_followups || 4} onChange={(e) => setForm({ ...form, max_followups: parseInt(e.target.value) || 4 })} />
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Ajouter une Note</Label>
            <Textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
            {lead && lead.notes && lead.notes.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                <p className="font-semibold mb-1">Historique :</p>
                <div className="max-h-24 overflow-y-auto space-y-1 pl-1">
                  {lead.notes.map((n, i) => (
                    <div key={i} className="border-l-2 pl-2 border-muted">
                      <span className="font-medium text-[10px]">{new Date(n.created_at).toLocaleDateString()}</span>
                      <span className="block">{n.content}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit">{lead ? 'Enregistrer' : 'Créer'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- DIALOGUE CONVERT ---
const ConvertDialog = ({ lead, open, onOpenChange, onConfirm, loading }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2"><ArrowRightLeft className="w-5 h-5" /> Convertir</DialogTitle>
      </DialogHeader>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button onClick={onConfirm} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Créer le client
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// --- COMPOSANT DIALOGUE DE RELANCE LEAD ---
const LeadFollowUpDialog = ({ lead, open, onOpenChange }) => {
  const { token } = useAuthStore();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && lead) {
      setFiles([]);
      fetch(`${API_URL}/leads/${lead.id || lead._id}/follow-up-template`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => { setSubject(data.subject || ''); setBody(data.body || ''); });
    }
  }, [open, lead, token]);

  const handleSend = async () => {
    if (!subject || !body) return toast.error('Sujet et message requis');
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('body', body);
      files.forEach(f => formData.append('files', f));

      const res = await fetch(`${API_URL}/leads/${lead.id || lead._id}/trigger-followup`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData, });
      if (!res.ok) throw new Error('Erreur lors de l\'envoi');
      toast.success('🚀 Relance envoyée avec succès !');
      onOpenChange(false);
    } catch (error) { toast.error(error.message); } finally { setIsLoading(false); }
  };

  if (!lead) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Relancer {lead.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1"><Label>Sujet</Label><Input value={subject} onChange={e => setSubject(e.target.value)} /></div>
          <div className="space-y-1"><Label>Message</Label><Textarea value={body} onChange={e => setBody(e.target.value)} rows={8} /></div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSend} disabled={isLoading}><Send className="w-4 h-4 mr-2" /> Envoyer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// --- PAGE PRINCIPALE ---
const LeadsPage = () => {
  const { showMetrics, toggleMetrics } = useUIStore();
  const { token } = useAuthStore();
  const queryClient = useQueryClient();

  const [manualFollowupDelayDays, setManualFollowupDelayDays] = useState(5);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [isDndReady, setIsDndReady] = useState(false);
  const [followUpLead, setFollowUpLead] = useState(null);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setIsDndReady(true));
    return () => cancelAnimationFrame(animation);
  }, []);

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['company-settings'],
    queryFn: async () => (await fetch(`${API_URL}/settings/company`, { headers: { Authorization: `Bearer ${token}` } })).json()
  });

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['leads'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      return res.json();
    },
    refetchOnWindowFocus: true,
  });

  // MUTATION POUR CHANGER LE SETTING GLOBAL
  const updateSettingsMutation = useMutation({
    mutationFn: async (newSettings) => {
      const res = await fetch(`${API_URL}/settings/company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newSettings)
      });
      if (!res.ok) throw new Error("Erreur serveur");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      toast.success('Paramètres enregistrés !');
    },
    onError: (err) => toast.error(err.message)
  });

  const runAutomation = async () => {
    const loadingToast = toast.loading("Vérification des séquences...");
    try {
      const res = await fetch(`${API_URL}/leads/run-automation-check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      toast.dismiss(loadingToast);
      toast.success(data.message);
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['leads']);
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error("Erreur technique");
    }
  };

  const { pipelineColumns, columnLeads, displayColumns, wonValue, totalValue, convRate } = useMemo(() => {
    const pipelineColumns = settings?.pipeline_stages || [];
    const validStageIds = pipelineColumns.map(c => c.id);
    const orphanLeads = leads.filter(l => !validStageIds.includes(l.status));
    const displayColumns = [...pipelineColumns];

    if (orphanLeads.length > 0) {
      displayColumns.unshift({ id: 'unclassified', name: `⚠️ Non classés (${orphanLeads.length})`, color: '#ef4444' });
    }

    const sortLeads = (leadsArray) => {
      return [...leadsArray].sort((a, b) => {
        let urgA = -1;
        if (!a.auto_followup_active && a.status !== 'won' && a.status !== 'lost') {
          const lastContactA = a.last_followup_date ? new Date(a.last_followup_date) : new Date(a.created_at || Date.now());
          const diffA = Math.ceil(Math.abs(new Date() - lastContactA) / (1000 * 60 * 60 * 24));
          if (diffA >= manualFollowupDelayDays) urgA = diffA;
        }

        let urgB = -1;
        if (!b.auto_followup_active && b.status !== 'won' && b.status !== 'lost') {
          const lastContactB = b.last_followup_date ? new Date(b.last_followup_date) : new Date(b.created_at || Date.now());
          const diffB = Math.ceil(Math.abs(new Date() - lastContactB) / (1000 * 60 * 60 * 24));
          if (diffB >= manualFollowupDelayDays) urgB = diffB;
        }

        if (urgA !== urgB) return urgB - urgA;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    };

    const columnLeads = {};
    pipelineColumns.forEach(col => { columnLeads[col.id] = sortLeads(leads.filter(ld => ld.status === col.id)); });
    if (orphanLeads.length > 0) { columnLeads['unclassified'] = sortLeads(orphanLeads); }

    const totalValue = leads.reduce((s, ld) => s + (ld.estimated_value || 0), 0);
    const wonStatusId = pipelineColumns.find(s => s.is_system && (s.id === 'won' || s.id.includes('won')))?.id || 'won';
    const wonValue = leads.filter(ld => ld.status === wonStatusId).reduce((s, ld) => s + (ld.estimated_value || 0), 0);
    const convRate = leads.length > 0 ? Math.round(leads.filter(ld => ld.status === wonStatusId).length / leads.length * 100) : 0;

    return { pipelineColumns, columnLeads, displayColumns, wonValue, totalValue, convRate };
  }, [leads, settings, manualFollowupDelayDays]);

  const createMutation = useMutation({ mutationFn: async (data) => (await fetch(`${API_URL}/leads`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(data) })).json(), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success('Lead créé'); setFormOpen(false); }, onError: (e) => toast.error(e.message || "Erreur création") });
  const updateMutation = useMutation({ mutationFn: async ({ id, data }) => { const res = await fetch(`${API_URL}/leads/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(data) }); if (!res.ok) throw new Error("Erreur mise à jour"); return res.json(); }, onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success('Lead mis à jour'); setFormOpen(false); }, onError: () => toast.error('Erreur mise à jour') });
  const deleteMutation = useMutation({ mutationFn: async (id) => (await fetch(`${API_URL}/leads/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success('Lead supprimé'); } });
  const convertMutation = useMutation({ mutationFn: async (id) => (await fetch(`${API_URL}/leads/${id}/convert`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })).json(), onSuccess: (data) => { queryClient.invalidateQueries({ queryKey: ['leads'] }); queryClient.invalidateQueries({ queryKey: ['clients'] }); toast.success(`Client "${data.client?.name}" créé`); setConvertLead(null); }, onError: (err) => toast.error(err.message) });

  const dismissAlertMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/leads/${id}/dismiss-alert`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Erreur serveur");
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success("Prospect marqué comme traité aujourd'hui !"); },
    onError: (error) => toast.error(error.message)
  });

  const handleSave = (data) => { if (editingLead) { updateMutation.mutate({ id: editingLead.id || editingLead._id, data }); } else { createMutation.mutate(data); } };
  const handleEdit = (lead) => { setEditingLead(lead); setFormOpen(true); };
  const handleDelete = (id) => { if (window.confirm('Supprimer ce lead ?')) deleteMutation.mutate(id); };
  const handleDismissAlert = (leadId) => { dismissAlertMutation.mutate(leadId); };

  // 🔴 GESTION DE LA SÉLECTION MULTIPLE
  const toggleSelectLead = (id) => {
    setSelectedLeads(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // 🔴 SUPPRESSION EN MASSE
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const handleBulkDelete = async () => {
    if (!window.confirm(`Vous êtes sur le point de supprimer ${selectedLeads.length} prospect(s) définitivement. Confirmer ?`)) return;
    setIsBulkDeleting(true);
    try {
      await Promise.all(selectedLeads.map(id =>
        fetch(`${API_URL}/leads/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
      ));
      toast.success(`${selectedLeads.length} prospects supprimés !`);
      setSelectedLeads([]);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    } catch (error) {
      toast.error("Erreur lors de la suppression en masse.");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const openRelanceModal = (leadId) => {
    const leadToRelance = leads.find(l => (l.id || l._id) === leadId);
    if (leadToRelance) {
      if (!leadToRelance.email) return toast.error("Veuillez ajouter une adresse email.");
      setFollowUpLead(leadToRelance);
      setIsFollowUpOpen(true);
    }
  };

  const handleDragEnd = (result) => {
    const { draggableId, destination, source } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    let targetStatus = destination.droppableId;
    if (targetStatus === 'unclassified') return;
    const lead = leads.find(ld => (ld.id === draggableId || ld._id === draggableId));
    if (!lead) return;
    const leadId = lead.id || lead._id;
    const isWonColumn = pipelineColumns.find(s => s.id === targetStatus)?.is_system && (targetStatus === 'won' || targetStatus.includes('won'));
    if (isWonColumn && !lead.converted_client_id) {
      updateMutation.mutate({ id: leadId, data: { status: targetStatus } });
      setTimeout(() => setConvertLead({ ...lead, status: targetStatus }), 300);
      return;
    }
    updateMutation.mutate({ id: leadId, data: { status: targetStatus } });
  };

  if (settingsLoading) return <Skeleton className="h-96 w-full rounded-lg" />;

  return (
    <div className="flex flex-col space-y-4 h-[calc(100vh-80px)] overflow-hidden relative" data-testid="leads-page">

      {/* HEADER + BOUTONS */}
      <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold font-heading">Prospection</h1>
          <p className="text-muted-foreground">Pipeline commercial et suivi</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">

          {/* 🔴 NOUVEAU BOUTON GLOBAL : PLAY / PAUSE POUR LES RELANCES AUTO */}
          <Button
            variant={settings?.global_auto_followup_active !== false ? "outline" : "destructive"}
            size="sm"
            onClick={() => updateSettingsMutation.mutate({
              company_name: settings.company_name,
              global_auto_followup_active: settings?.global_auto_followup_active === false
            })}
            disabled={settingsLoading || updateSettingsMutation.isPending}
            className={`h-9 font-bold transition-all ${settings?.global_auto_followup_active !== false
              ? "border-green-500 text-green-700 bg-green-50 hover:bg-green-100 hover:text-green-800 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
              : "bg-red-500 hover:bg-red-600 text-white shadow-md animate-pulse"
              }`}
            title="Couper ou réactiver toutes les relances automatiques"
          >
            {settings?.global_auto_followup_active !== false ? (
              <><PlayCircle className="w-4 h-4 mr-2" /> Relances Auto: ON</>
            ) : (
              <><StopCircle className="w-4 h-4 mr-2" /> Relances Auto: PAUSE</>
            )}
          </Button>

          {/* Le reste des boutons */}
          <Button variant="ghost" size="icon" onClick={() => setShowSettings(!showSettings)} title="Paramètres d'alerte">
            <Settings2 className="w-4 h-4 text-muted-foreground" />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleMetrics} title={showMetrics ? "Masquer les chiffres" : "Afficher les statistiques"}>
            {showMetrics ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
          </Button>
          <Button onClick={() => { setEditingLead(null); setFormOpen(true); }} disabled={settingsLoading} className="h-9">
            <Plus className="w-4 h-4 mr-2" /> Nouveau lead
          </Button>
        </div>
      </div>

      {/* PANNEAU DE PARAMÉTRAGE (Désormais uniquement dédié au slider de délai) */}
      {showSettings && (
        <Card className="shrink-0 bg-muted/30 border-dashed animate-in fade-in zoom-in-95 duration-200">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-1">
              <h4 className="font-semibold flex items-center gap-2">
                <BellRing className="w-4 h-4 text-red-500" />
                Seuil d'alerte de relance manuelle
              </h4>
              <p className="text-xs text-muted-foreground">
                Afficher une cloche rouge si le prospect n'a pas été contacté depuis plus de {manualFollowupDelayDays} jours.
              </p>
            </div>
            <div className="flex-1 max-w-sm flex items-center gap-4">
              <span className="text-xs font-medium w-8 text-right">1 j</span>
              <Slider
                value={[manualFollowupDelayDays]}
                min={1} max={30} step={1}
                onValueChange={(v) => setManualFollowupDelayDays(v[0])}
                className="flex-1"
              />
              <span className="text-xs font-medium w-8">{manualFollowupDelayDays} j</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* METRICS DASHBOARD */}
      {showMetrics ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 shrink-0 animate-in fade-in slide-in-from-top-2 duration-300">
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Total leads</p><p className="text-xl font-bold">{leads.length}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Pipeline</p><p className="text-xl font-bold">{formatCurrency(totalValue)}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Gagné</p><p className="text-xl font-bold text-emerald-500">{formatCurrency(wonValue)}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Conversion</p><p className="text-xl font-bold">{convRate}%</p></CardContent></Card>
        </div>
      ) : (
        <div className="shrink-0 mb-2">
          <div className="p-6 rounded-xl border border-dashed border-primary/20 bg-primary/5 text-center flex flex-col items-center justify-center gap-2 animate-in fade-in zoom-in-95 duration-300">
            <span className="text-2xl">🎯</span>
            <h3 className="font-heading font-semibold text-lg text-primary">Mode Focus Activé</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Oubliez les chiffres pour l'instant. Concentrez-vous uniquement sur la qualité de vos échanges !
            </p>
          </div>
        </div>
      )}

      <div className="shrink-0 flex flex-wrap items-center gap-6 px-3 text-xs text-muted-foreground bg-card py-2.5 rounded-lg border shadow-sm w-fit">
        <span className="font-semibold text-foreground mr-1">Légende :</span>
        <div className="flex items-center gap-2">
          <BellRing className="w-3.5 h-3.5 text-red-500" />
          <span className="text-red-600 font-medium">À Relancer</span>
        </div>
        <div className="flex items-center gap-2">
          <PlayCircle className="w-3.5 h-3.5 text-blue-500" />
          <span>Automatisation</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-emerald-100 border border-emerald-500"></div>
          <span>Contact récent</span>
        </div>
      </div>

      {/* KANBAN BOARD */}
      {leadsLoading || !isDndReady ? <Skeleton className="flex-1 w-full rounded-lg" /> : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex-1 min-h-0 flex gap-4 overflow-x-auto pb-4 pr-4">
            {displayColumns.map(col => (
              <KanbanColumn
                key={col.id}
                column={col}
                leads={columnLeads[col.id] || []}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onManualRelance={openRelanceModal}
                onDismissAlert={handleDismissAlert}
                isOrphan={col.id === 'unclassified'}
                manualFollowupDelayDays={manualFollowupDelayDays}
                selectedLeads={selectedLeads}
                onToggleSelect={toggleSelectLead}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      {/* 🔴 BARRE D'ACTION FLOTTANTE (MULTI-SÉLECTION) */}
      {selectedLeads.length > 0 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-4 z-50 animate-in slide-in-from-bottom-5 fade-in">
          <span className="font-medium text-sm">{selectedLeads.length} lead(s) sélectionné(s)</span>
          <div className="w-px h-5 bg-slate-700"></div>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-900/50 h-8"
            onClick={handleBulkDelete}
            disabled={isBulkDeleting}
          >
            {isBulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Supprimer la sélection
          </Button>
          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8 rounded-full" onClick={() => setSelectedLeads([])}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      <LeadFormDialog lead={editingLead} open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditingLead(null); }} onSave={handleSave} columns={pipelineColumns} />
      <ConvertDialog lead={convertLead} open={!!convertLead} onOpenChange={(o) => { if (!o) setConvertLead(null); }} onConfirm={() => convertMutation.mutate(convertLead.id || convertLead._id)} loading={convertMutation.isPending} />

      <LeadFollowUpDialog
        lead={followUpLead}
        open={isFollowUpOpen}
        onOpenChange={(open) => {
          setIsFollowUpOpen(open);
          if (!open) {
            setFollowUpLead(null);
            queryClient.invalidateQueries({ queryKey: ['leads'] });
          }
        }}
      />
    </div>
  );
};

export default LeadsPage;